import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GPX_FEATURES } from '@rsos/gainsight';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  useLazyGetConversationMetadataQuery,
  useLazyCreateConversationQuery,
  useCreateConversationOutboundSMSMutation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import useDisplayPhoneNumber from '../../../../hooks/useDisplayPhoneNumber';
import { ChatContextProvider } from '../../CallChats/ChatContext';
import TF911ChatHeader from '../../CallChats/TF911ChatBox/TF911ChatHeader';
import {
  trackStartTF911Response,
  trackLiveTF911CardClick,
} from '../../CallChats/textFrom911Trackings';
import LiveChatCard from './LiveChatCard';

const CHAT_TAB = 'chat';

/**
 * Standard TF911 chat card component that used in the ChatCard.js component.
 */
const TF911ChatCard = () => {
  const dispatch = useDispatch();

  const [showPreconfiguredMessages, setShowPreconfiguredMessages] =
    useState(false);

  const hasLocationsToRender = useHasLocationsToRender();

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const displayPhoneNumber = useDisplayPhoneNumber(selectedCallerID);

  const conversationID = useSelectedTextFrom911ConversationID(selectedCallerID);

  const conversation = useSelectedTextFrom911Conversation(selectedCallerID);

  const isOutboundSMSConversation = useMemo(
    () => conversation?.data?.isOutboundSMS,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const [triggerGetConversationMetaData] =
    useLazyGetConversationMetadataQuery();

  const [triggerCreateConversation, createConversationResult] =
    useLazyCreateConversationQuery();

  const [
    triggerCreateOutboundSMSConversation,
    createOutboundSMSConversationResult,
  ] = useCreateConversationOutboundSMSMutation();

  const handleSetChatTab = useCallback(
    () => dispatch(setSelectedTab(CHAT_TAB)),
    [dispatch],
  );

  const handleLiveChatCardClick = useCallback(() => {
    handleSetChatTab();
    trackLiveTF911CardClick({
      feature: isOutboundSMSConversation
        ? GPX_FEATURES.TF911_OUTBOUND
        : GPX_FEATURES.TF911,
      conversationId: conversationID,
    });
  }, [conversationID, handleSetChatTab, isOutboundSMSConversation]);

  const handleClickShowAccordion = useCallback(() => {
    setShowPreconfiguredMessages(prev => !prev);
  }, []);

  const handleStartConversation = useCallback(
    message => {
      if (!hasLocationsToRender) {
        return triggerCreateOutboundSMSConversation({
          callerID: selectedCallerID,
          message,
          orgName,
          custom: {},
        }).then(response => {
          if (response?.data?.conversation_id) {
            setShowPreconfiguredMessages(false);
          } else {
            if (response.error?.status === 409) {
              return triggerGetConversationMetaData({
                callerID: selectedCallerID,
                orgName,
                custom: { isOutboundSMS: true },
              }).then(response => {
                if (response?.isSuccess) {
                  setShowPreconfiguredMessages(false);
                  handleSetChatTab();
                  return response;
                }
              });
            }
          }

          return response;
        });
      }
      return triggerCreateConversation({
        callerID: selectedCallerID,
        orgName,
        startMessage: message,
      }).then(response => {
        if (response.isSuccess) {
          setShowPreconfiguredMessages(false);

          handleSetChatTab();
        }
        return response;
      });
    },
    [
      hasLocationsToRender,
      orgName,
      selectedCallerID,
      handleSetChatTab,
      triggerCreateConversation,
      triggerCreateOutboundSMSConversation,
      triggerGetConversationMetaData,
    ],
  );

  const showStartChat = useMemo(
    () => !conversationID || isConversationEnded,
    [conversationID, isConversationEnded],
  );

  useEffect(
    () => {
      if (selectedCallerID) {
        triggerGetConversationMetaData({
          callerID: selectedCallerID,
          orgName,
          custom: { isOutboundSMS: isOutboundSMSConversation },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCallerID],
  );

  // track start TF911 response
  useEffect(() => {
    if (['fulfilled', 'rejected'].includes(createConversationResult.status)) {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911,
        responseSuccess: createConversationResult.status === 'fulfilled',
        conversationId: createConversationResult?.data?.conversation_id,
      });
    }
  }, [createConversationResult]);

  useEffect(() => {
    if (
      ['fulfilled', 'rejected'].includes(
        createOutboundSMSConversationResult.status,
      )
    ) {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911_OUTBOUND,
        responseSuccess:
          createOutboundSMSConversationResult.status === 'fulfilled',
        conversationId:
          createOutboundSMSConversationResult?.data?.conversation_id,
      });
    }
  }, [createOutboundSMSConversationResult]);

  return (
    <div data-name="tf911-chat-card">
      {showStartChat ? (
        <ChatContextProvider chatType="textFrom911">
          <TF911ChatHeader
            isForOverview
            onClickShowAccordion={handleClickShowAccordion}
            onStartConversation={handleStartConversation}
            showPreconfiguredMessages={showPreconfiguredMessages}
            showStartChat={showStartChat}
            gpxFeature={
              isOutboundSMSConversation
                ? GPX_FEATURES.TF911_OUTBOUND
                : GPX_FEATURES.TF911
            }
          />
        </ChatContextProvider>
      ) : (
        <LiveChatCard
          callerID={displayPhoneNumber}
          onClick={handleLiveChatCardClick}
        />
      )}
    </div>
  );
};

export default TF911ChatCard;
